import {useRef, useState, type PointerEvent, type RefObject} from 'react';
import {anchorFromRect, callDock, DOCK_MARGIN} from './callDock';

const DRAG_THRESHOLD = 4;

export interface DockPoint {
    readonly left: number;
    readonly top: number;
}

interface DragState {
    readonly pointerId: number;
    readonly startX: number;
    readonly startY: number;
    readonly offsetX: number;
    readonly offsetY: number;
    readonly width: number;
    readonly height: number;
    moved: boolean;
}

export const clampToViewport = (left: number, top: number, width: number, height: number): DockPoint => ({
    left: Math.min(Math.max(left, DOCK_MARGIN), window.innerWidth - width - DOCK_MARGIN),
    top: Math.min(Math.max(top, DOCK_MARGIN), window.innerHeight - height - DOCK_MARGIN),
});

export const dockAt = ({left, top}: DockPoint, width: number, height: number): void => {
    callDock.set(anchorFromRect({left, top, width, height}, {width: window.innerWidth, height: window.innerHeight}));
};

export interface DockDrag {
    /** Where the element follows the pointer while it is dragged; undefined while it sits in the dock. */
    readonly position?: DockPoint;
    readonly onPointerDown: (event: PointerEvent<HTMLElement>) => void;
    readonly onPointerMove: (event: PointerEvent<HTMLElement>) => void;
    readonly onPointerEnd: (event: PointerEvent<HTMLElement>) => void;
    /** True once after a drag, so the click that ends it does not also press what is under the pointer. */
    readonly consumeClick: () => boolean;
}

/**
 * Drags a docked call surface. While dragging it follows the pointer; on release the shared call dock moves to the
 * nearest corner offset, so the pill, the dialer and every call bubble open in the same place afterwards.
 */
export const useDockDrag = (container: RefObject<HTMLElement>, onDragStart?: () => void): DockDrag => {
    const drag = useRef<DragState>();
    const suppressClick = useRef(false);
    const [position, setPosition] = useState<DockPoint>();

    const place = (event: PointerEvent<HTMLElement>, state: DragState): DockPoint =>
        clampToViewport(event.clientX - state.offsetX, event.clientY - state.offsetY, state.width, state.height);

    const onPointerDown = (event: PointerEvent<HTMLElement>) => {
        const rect = container.current?.getBoundingClientRect();
        if (event.button !== 0 || !rect) {
            return;
        }
        // A drag whose closing click never arrived must not swallow this press.
        suppressClick.current = false;
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top,
            width: rect.width,
            height: rect.height,
            moved: false,
        };
    };

    const onPointerMove = (event: PointerEvent<HTMLElement>) => {
        const state = drag.current;
        if (!state || state.pointerId !== event.pointerId) {
            return;
        }
        if (!state.moved && Math.hypot(event.clientX - state.startX, event.clientY - state.startY) < DRAG_THRESHOLD) {
            return;
        }
        if (!state.moved) {
            state.moved = true;
            onDragStart?.();
        }
        setPosition(place(event, state));
    };

    const onPointerEnd = (event: PointerEvent<HTMLElement>) => {
        const state = drag.current;
        if (!state || state.pointerId !== event.pointerId) {
            return;
        }
        drag.current = undefined;
        if (!state.moved) {
            return;
        }
        dockAt(place(event, state), state.width, state.height);
        suppressClick.current = true;
        setPosition(undefined);
    };

    const consumeClick = () => {
        if (!suppressClick.current) {
            return false;
        }
        suppressClick.current = false;
        return true;
    };

    return {position, onPointerDown, onPointerMove, onPointerEnd, consumeClick};
};
